import React, { useEffect, useId, useState } from 'react';
import { Box, MenuItem, Popover, TextField, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';

import { getSentimentFeedback, putSentimentFeedback } from 'services/callScribe';
import {
  CallScribeCustomerOutcome,
  CallScribeSentiment,
  CallScribeSentimentFeedbackVerdict,
  ICallScribeCallListItem,
  IUpsertSentimentFeedbackRequest,
} from 'services/callScribe/types';
import { ESCALATION_LABEL, SENTIMENT_LABEL } from 'components/CallScribe/callLogColumns';
import { buildInsightsHeader } from 'components/CallScribe/EmotionTimeline/emotionSpectrumModel';
import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';
import usePaperStyles, { paperFieldRules } from 'components/CallScribe/paperStyles';

const OUTCOME_OPTIONS: Array<{ value: CallScribeCustomerOutcome; label: string }> = [
  { value: CallScribeCustomerOutcome.Yes, label: 'Yes' },
  { value: CallScribeCustomerOutcome.No, label: 'No' },
  { value: CallScribeCustomerOutcome.Unclear, label: 'Unclear' },
];

const SENTIMENT_OPTIONS: Array<{ value: CallScribeSentiment; label: string }> = [
  { value: CallScribeSentiment.Positive, label: 'Positive' },
  { value: CallScribeSentiment.Neutral, label: 'Neutral' },
  { value: CallScribeSentiment.Negative, label: 'Negative' },
  { value: CallScribeSentiment.Mixed, label: 'Mixed' },
];

const RISK_OPTIONS = [0, 1, 2, 3];

export type CallInsightsHeaderSurface = 'paper' | 'ground';

export interface CallInsightsHeaderProps {
  call: ICallScribeCallListItem;
  surface?: CallInsightsHeaderSurface;
}

export interface ICallInsightsSectionProps extends CallInsightsHeaderProps {
  variant?: 'stack' | 'section';
  title?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}

const useStyles = makeStyles<Theme, { surface: CallInsightsHeaderSurface }>(theme => {
  const t = csTokens(theme);
  const onGround = ({ surface }: { surface: CallInsightsHeaderSurface }): boolean => surface === 'ground';
  return {
    root: { display: 'flex', flexDirection: 'column', gap: 8 },
    header: {
      ...CS_FONT.small,
      margin: 0,
      fontWeight: CS_WEIGHT.strong,
      color: props => (onGround(props) ? t.onGround : t.text),
    },
    vote: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: 6,
      rowGap: 0,
      minWidth: 0,
    },
    voteLabel: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      marginRight: 2,
      color: props => (onGround(props) ? t.onGroundMuted : t.text2),
    },
    voteButton: {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      height: 32,
      padding: '0 10px',
      borderRadius: 8,
      border: props => `1px solid ${onGround(props) ? t.glassBorder : t.controlLine}`,
      background: 'transparent',
      color: props => (onGround(props) ? t.onGround : t.text),
      fontFamily: 'inherit',
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out, border-color 150ms ease-out, color 150ms ease-out',
      '& svg': { fontSize: 16 },
      '&::before': { content: '""', position: 'absolute', inset: '-6px -2px' },
      '&:hover': { background: props => (onGround(props) ? t.glass : t.surface3) },
      '&:focus-visible': {
        outline: props => `2px solid ${onGround(props) ? t.stageFocus : t.focus}`,
        outlineOffset: 2,
      },
    },
    voteUp: {
      '&[aria-pressed="true"]': { background: t.posBg, borderColor: 'transparent', color: t.pos },
    },
    voteDown: {
      '&[aria-pressed="true"]': { background: t.negBg, borderColor: 'transparent', color: t.neg },
    },
    voteStatus: {
      ...CS_FONT.caption,
      flexBasis: '100%',
      textAlign: 'right',
      color: props => (onGround(props) ? t.onGroundMuted : t.text2),
      '&:not(:empty)': { marginTop: 6 },
    },
    popover: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      padding: 16,
      minWidth: 260,
      background: t.surface,
      color: t.text,
    },
    popoverTitle: {
      ...CS_FONT.small,
      margin: 0,
      fontWeight: CS_WEIGHT.heavy,
      color: t.text,
    },
    popoverField: paperFieldRules(theme),
    popoverSubmit: {
      alignSelf: 'flex-end',
      minHeight: 40,
      padding: '0 16px',
      border: 'none',
      borderRadius: 10,
      background: t.brand,
      color: t.brandInk,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
  };
});

export const InsightLine: React.FC<CallInsightsHeaderProps> = ({ call, surface = 'paper' }) => {
  const classes = useStyles({ surface });
  const header = buildInsightsHeader(call);
  return (
    <p className={classes.header}>
      {header.kind === 'legacy' ? SENTIMENT_LABEL[call.sentiment] : header.text}
    </p>
  );
};

export const SentimentVote: React.FC<CallInsightsHeaderProps> = ({ call, surface = 'paper' }) => {
  const classes = useStyles({ surface });
  const [verdict, setVerdict] = useState<CallScribeSentimentFeedbackVerdict | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [correctedSentiment, setCorrectedSentiment] = useState<CallScribeSentiment>(
    CallScribeSentiment.Neutral,
  );
  const [correctedOutcome, setCorrectedOutcome] = useState<CallScribeCustomerOutcome>(
    CallScribeCustomerOutcome.Unclear,
  );
  const [correctedRisk, setCorrectedRisk] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSentimentFeedback(call.id)
      .then(res => {
        if (!cancelled && res?.data) {
          setVerdict(res.data.verdict);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [call.id]);

  const submit = async (
    verdictToSend: CallScribeSentimentFeedbackVerdict,
    extra: Partial<IUpsertSentimentFeedbackRequest> = {},
  ): Promise<void> => {
    const previous = verdict;
    setVerdict(verdictToSend);
    setError(null);
    try {
      await putSentimentFeedback(call.id, { verdict: verdictToSend, ...extra });
    } catch {
      setVerdict(previous);
      setError('Could not save your feedback. Please try again.');
    }
  };

  const up = verdict === CallScribeSentimentFeedbackVerdict.Up;
  const down = verdict === CallScribeSentimentFeedbackVerdict.Down;
  const statusText = error ?? (up
    ? 'Saved: the judgment was right.'
    : down
      ? 'Saved: the judgment was wrong.'
      : '');

  return (
    <>
      <div role="group" aria-label="Was the sentiment judgment right?" className={classes.vote}>
        <span className={classes.voteLabel}>Was this right?</span>
        <button
          type="button"
          className={`${classes.voteButton} ${classes.voteUp}`}
          aria-label="Yes, the judgment is right"
          aria-pressed={up}
          onClick={() => {
            void submit(CallScribeSentimentFeedbackVerdict.Up);
          }}
        >
          {up ? <ThumbUpAltIcon aria-hidden /> : <ThumbUpOffAltIcon aria-hidden />}
          Yes
        </button>
        <button
          type="button"
          className={`${classes.voteButton} ${classes.voteDown}`}
          aria-label="No, the judgment is wrong"
          aria-pressed={down}
          aria-haspopup="dialog"
          onClick={e => { setAnchorEl(e.currentTarget); }}
        >
          {down ? <ThumbDownAltIcon aria-hidden /> : <ThumbDownOffAltIcon aria-hidden />}
          No
        </button>
        <span role="status" aria-live="polite" className={classes.voteStatus}>{statusText}</span>
      </div>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ role: 'dialog', 'aria-label': 'What was the call really like?' }}
      >
        <Box className={classes.popover}>
          <p className={classes.popoverTitle}>What was the call really like?</p>
          <TextField
            select
            size="small"
            className={classes.popoverField}
            label="Actual customer sentiment"
            InputLabelProps={{ shrink: true }}
            value={correctedSentiment}
            onChange={e => { setCorrectedSentiment(Number(e.target.value)); }}
          >
            {SENTIMENT_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            className={classes.popoverField}
            label="Actual outcome"
            InputLabelProps={{ shrink: true }}
            value={correctedOutcome}
            onChange={e => { setCorrectedOutcome(Number(e.target.value)); }}
          >
            {OUTCOME_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            className={classes.popoverField}
            label="Actual escalation risk"
            InputLabelProps={{ shrink: true }}
            value={correctedRisk}
            onChange={e => { setCorrectedRisk(Number(e.target.value)); }}
          >
            {RISK_OPTIONS.map(r => (
              <MenuItem key={r} value={r}>
                {ESCALATION_LABEL[r]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            multiline
            minRows={2}
            className={classes.popoverField}
            label="Comment"
            InputLabelProps={{ shrink: true }}
            value={comment}
            onChange={e => { setComment(e.target.value); }}
          />
          <button
            type="button"
            className={classes.popoverSubmit}
            onClick={() => {
              void submit(CallScribeSentimentFeedbackVerdict.Down, {
                correctedCustomerSentiment: correctedSentiment,
                correctedOutcome,
                correctedEscalationRisk: correctedRisk,
                comment: comment || null,
              });
              setAnchorEl(null);
            }}
          >
            Submit
          </button>
        </Box>
      </Popover>
    </>
  );
};

const CallInsightsHeader: React.FC<ICallInsightsSectionProps> = ({
  call,
  surface = 'paper',
  variant = 'stack',
  title = 'Summary',
  children,
  footer,
}) => {
  const classes = useStyles({ surface });
  const paper = usePaperStyles();
  const headingId = useId();
  if (variant === 'section') {
    return (
      <section className={paper.section} aria-labelledby={headingId}>
        <div className={paper.sectionHead}>
          <h3 id={headingId} className={paper.sectionLabel}>{title}</h3>
          <SentimentVote call={call} surface={surface} />
        </div>
        {children}
        {buildInsightsHeader(call).kind === 'legacy' ? null : <InsightLine call={call} surface={surface} />}
        {footer}
      </section>
    );
  }
  return (
    <div className={classes.root}>
      <InsightLine call={call} surface={surface} />
      <SentimentVote call={call} surface={surface} />
    </div>
  );
};

export default CallInsightsHeader;
